import React, { useContext, useEffect } from 'react';

import { useStorage } from '~Components/Storage';
import { v4 as uuid } from 'uuid';
import { datadogLogs } from '@datadog/browser-logs';
import { useDeviceInfo, useLogger, useSelector } from '~Hooks';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useDeviceSettingsUniverse } from '~Components/DeviceSettingsUniverse';
import { SerializedState } from '@baemingo/device-settings-events/dist/types';
import MigrationManager from './MigrationManager';
import {
  ClaimDevice,
  ClaimDeviceVariables,
  CreateDevice,
  CreateDeviceVariables,
  GetDevices,
  GetDevices_operativeViewer_devices,
  GetVenue,
  UpdateDevice,
  UpdateDeviceVariables,
} from './__queries__';

const FORCED_REFUND_IDS = [
  'ckrkfzjez0hmc0815lmp6bzfe', // East
  'ckn9yyhm0amra0b57st8t2crj', // Trattorian
  'ckoseuoxc0k9y0872pnpjum2l', // Prinsen
  'cks8on7szo7k70a86rldmyvrn', // Pas D Art
  'ckkza1oockxdn0990guabt1jh', // Il Tempo
  'cknn9315m9jpn0815ek23r3dr', // Ricordi
  'clf6ub8auus4g0c408axxjk0z', // Juno
  'clgga4iswu31x0c4049bxefy2', // Trattorian Sorellina
  'ckowuegikigmk0a67xjkg7fje', // Villa Godthem
  'ck2xhvggr4vih0109zwxdjkrl', // Brasseriegruppen
];

interface DeviceContext {
  universe: SerializedState | null;
  /** Unknown since we don't know all the types this may contain */
  remotelyControlledData: Record<string, unknown>;
  organizationId: string | null;

  activeDevice?: GetDevices_operativeViewer_devices | null;
  allDevices: GetDevices_operativeViewer_devices[];
  availableDevices: GetDevices_operativeViewer_devices[];

  createDevice(name: string): Promise<string | null>;
  updateDevice(
    id: string,
    posDeviceId: string,
    complianceId?: string, // complianceId is only needed for Swedish companies (Skatteverket related, same as taxComplianceControlUnitSerial)
    productionNumber?: string, // productionNumber is only needed for Swedish companies (Skatteverket related, same as taxComplianceProductionNumber)
  ): Promise<{ success: boolean; error?: string }>;

  claim(id: string): Promise<{ success: boolean; error?: string }>;

  refetch: () => void; // Used if we claim a license for a device
}

// @ts-ignore We ignore here so we can null check and alert if there's no context set
const Context = React.createContext<DeviceContext>(null);

const DEVICES_QUERY = gql`
  query GetDevices {
    operativeViewer {
      id

      devices {
        id
        ownerClientId
        complianceId
        posDeviceId
        receiptCounter
        name
        lastClaimedAt
        isMigrated
        activeLicenses {
          id
          product
        }
      }
    }
  }
`;

const VENUE_QUERY = gql`
  query GetVenue($id: ID!) {
    venue: Venue(id: $id) {
      id
      organizationId
    }
  }
`;

const CREATE_DEVICE_MUTATION = gql`
  mutation CreateDevice($data: CreateDeviceInput!) {
    createDevice(data: $data) {
      id
      # Name is used for logging purposes to easier identify the device
      name
    }
  }
`;

const UPDATE_DEVICE_MUTATION = gql`
  mutation UpdateDevice(
    $id: String!
    $complianceId: String
    $productionNumber: String
    $posDeviceId: String
  ) {
    updateDevice(
      id: $id
      complianceId: $complianceId
      productionNumber: $productionNumber
      posDeviceId: $posDeviceId
    ) {
      success
      message
      error
    }
  }
`;

const CLAIM_DEVICE_MUTATION = gql`
  mutation ClaimDevice($id: String!, $clientId: String!) {
    success: claimDevice(id: $id, clientId: $clientId)
  }
`;

/**
 * Device Provider
 *
 * Handles all device related logic, including:
 * - Creating a new device
 * - Claiming an existing device
 * - Device data
 * - Device settings
 */
const DeviceProvider: React.FC = ({ children }) => {
  const logger = useLogger('device-provider');
  const deviceUniverse = useDeviceSettingsUniverse();
  const deviceInfo = useDeviceInfo();
  const [storage, updateStorage, storageLoading] = useStorage();

  const venueId = useSelector((state) => state.venueId) as string;
  const companyFlags = useSelector((state) => state.companyFlags);

  const [createDeviceMutation] = useMutation<CreateDevice, CreateDeviceVariables>(
    CREATE_DEVICE_MUTATION,
  );
  const [updateDeviceMutation] = useMutation<UpdateDevice, UpdateDeviceVariables>(
    UPDATE_DEVICE_MUTATION,
  );
  const [claimDeviceMutation] = useMutation<ClaimDevice, ClaimDeviceVariables>(
    CLAIM_DEVICE_MUTATION,
  );

  const venueQuery = useQuery<GetVenue>(VENUE_QUERY, {
    variables: {
      id: venueId,
    },
    skip: !venueId,
  });

  const devicesQuery = useQuery<GetDevices>(DEVICES_QUERY);
  const allDevices = devicesQuery?.data?.operativeViewer?.devices || [];
  const availableDevices = allDevices.filter((d) => !d.ownerClientId);

  /** Device this physical device is assigned to */
  const device = allDevices.find((d) => d.id === storage.deviceId);

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: DeviceProvider');
    return () => {
      mountLogger.info('Unmounted: DeviceProvider');
    };
  }, []);

  /**
   * All clients/devices will have a unique "Client ID" assigned.
   * This is client-side-generated UUID and is set once and then stays the same forever.
   */
  useEffect(() => {
    if (storageLoading) {
      return;
    }

    if (!storage.clientId) {
      updateStorage({
        clientId: uuid(),
      });
    }
  }, [storage.clientId, storageLoading]);

  /**
   * If device name starts with "S1F2 or "S1E2" we know it's a castle
   * terminal. If no adyen terminal has been added, we can simply assign ourselves.
   */
  useEffect(() => {
    if (storageLoading) {
      return;
    }

    if (!deviceInfo) {
      return;
    }

    const name = deviceInfo.deviceName;
    const castleTerminal = name.startsWith('S1F2') || name.startsWith('S1E2');

    // If it's not a castle terminal or we already have an adyen terminal
    if (!castleTerminal || storage.adyenTerminals.length) {
      return;
    }

    /**
     * We add the terminal as 127.0.0.1 here, since we want to connect to
     * "ourselves".
     */
    updateStorage({
      adyenTerminals: [
        {
          hasPrinter: true,
          IP: '127.0.0.1',
          POIID: name,
        },
      ],
    });
  }, [deviceInfo, storage.adyenTerminals, storageLoading]);

  /**
   * If there's a device with our Client ID assigned to it, but we dont have a
   * device, then it means that it was assigned in back-office and we should
   * connect to it client-side.
   */
  useEffect(() => {
    if (storageLoading || devicesQuery.loading) {
      return;
    }

    const deviceWithOurClientId = allDevices.find((d) => d.ownerClientId === storage.clientId);

    // Nothing to see here, no devices found unless there is a migration to be done
    if (!deviceWithOurClientId) {
      const foundDevice = allDevices.find((d) => d.posDeviceId === storage.taxComplianceDeviceId);

      // If we found a device with our taxComplianceDeviceId, claim it
      if (
        companyFlags.useNewLicenses &&
        storage.taxComplianceDeviceId &&
        foundDevice &&
        !foundDevice.ownerClientId
      ) {
        logger.info('Migrating to new licenses - claiming device', foundDevice);

        claim(foundDevice.id);
      }
      return;
    }

    // We're already assigned to it, do nothing
    if (deviceWithOurClientId.id === storage.deviceId) {
      return;
    }

    logger.info('Device assigned to client', {
      deviceId: deviceWithOurClientId.id,
      clientId: storage.clientId,
    });

    // Exciting! Use it!
    updateStorage({
      deviceId: deviceWithOurClientId.id,
    });
  }, [
    storage.clientId,
    storage.deviceId,
    allDevices,
    devicesQuery.loading,
    storageLoading,
    companyFlags.useNewLicenses,
  ]);

  /**
   * This effect checks if the device has been un-assigned from our client.
   *
   * If that's the case, we clear the device ID from storage.
   */
  useEffect(() => {
    if (storageLoading || devicesQuery.loading) {
      return;
    }

    if (!storage.deviceId) {
      return;
    }

    // Device not found could be that a browser was signed with a storage.deviceId that belongs to a different venue
    if (!device) {
      logger.warn('Device not found', {
        deviceId: storage.deviceId,
        allDevices,
      });
      return;
    }

    // The device is still ours, do nothing
    if (device.ownerClientId === storage.clientId) {
      return;
    }

    logger.info('Device unassigned from client');

    updateStorage({
      deviceId: null,
    });
  }, [storage.deviceId, devicesQuery, storageLoading]);

  /**
   * Ensure all datadog logs use the unique device and client IDs
   * (this one is for you, future developer that is debugging something)
   */
  useEffect(() => {
    if (storage.clientId) {
      datadogLogs.addLoggerGlobalContext('clientId', storage.clientId);
    }

    if (storage.deviceId) {
      datadogLogs.addLoggerGlobalContext('deviceId', storage.deviceId);
    }
  }, [storage.clientId, storage.deviceId]);

  /**
   * Creates the actual new device (to get complianceId & posDeviceId is handled in ClaimDevice component)
   */
  async function createDevice(name: string): Promise<string | null> {
    try {
      const result = await createDeviceMutation({
        variables: {
          data: {
            name,
            venueId,
          },
        },
      });

      if (result.data && result.data.createDevice) {
        const deviceId = result.data.createDevice.id;

        logger.info('New device created successfully', {
          id: deviceId,
          name,
        });

        await devicesQuery.refetch();

        return deviceId as string;
      }
    } catch (error) {
      logger.error('Failed to create device', { errorObject: error });
    }
    return null;
  }

  async function updateDevice(
    deviceId: string,
    posDeviceId: string,
    complianceId?: string,
    productionNumber?: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await updateDeviceMutation({
        variables: {
          id: deviceId,
          complianceId,
          productionNumber,
          posDeviceId,
        },
      });

      if (!res.data?.updateDevice.success) {
        throw new Error(res.data?.updateDevice.error);
      }
      return { success: true };
    } catch (error) {
      logger.error('Failed to update device', { errorObject: error });
      return { success: false, error: typeof error === 'string' ? error : 'Something went wrong' };
    }
  }

  /**
   * Claim an available device to be used on this client
   *
   * @param id The ID of the Device to claim
   */
  async function claim(id: string): Promise<{ success: boolean; error?: string }> {
    logger.info('Claiming device. ID: ' + id);

    if (!storage.clientId) {
      logger.error('Client ID not set, cannot claim device');
      return { success: false, error: 'No Client ID found in storage' };
    }

    const result = await claimDeviceMutation({
      variables: {
        id,
        clientId: storage.clientId,
      },
    });

    /** Refetch to prevent the useEffect to auto-select wrong deviceId  */
    await devicesQuery.refetch();

    if (result?.data?.success) {
      updateStorage({
        deviceId: id,
      });

      return { success: true };
    } else {
      logger.error('Claiming device failed');

      return {
        success: false,
        error: 'Claiming device failed',
      };
    }
  }

  const value: DeviceContext = {
    universe: deviceUniverse,
    /** Ensure no values gets set with undefined (this can happen if a remotely controlled setting gets removed) */
    remotelyControlledData: Object.entries(deviceUniverse?.storage ?? {})
      .filter(([_, v]) => v !== undefined)
      .reduce((acc, [k, v]) => {
        acc[k] = v;
        return acc;
      }, {} as Record<string, unknown>),

    organizationId: venueQuery.data?.venue?.organizationId || null,

    activeDevice: device,
    allDevices,
    availableDevices,

    createDevice,
    updateDevice,
    claim,

    refetch: devicesQuery.refetch,
  };

  return (
    <Context.Provider value={value}>
      <MigrationManager />
      {children}
    </Context.Provider>
  );
};

export const useDeviceHandler = () => {
  const value = useContext(Context);

  if (!value) {
    throw new Error('Missing Provider for Device context');
  }

  return value;
};

export default DeviceProvider;
